import { httpClient } from '../common/httpclient';

/**
 * Serviço específico para manipulação de produtos genéricos
 * Centraliza toda a lógica relacionada a produtos genéricos
 */
class GenericProductService {
    /**
     * Adiciona um produto genérico ao carrinho
     * @param {Object} item - Dados do produto genérico
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async addItem(item, saleId) {
        
        const response = await httpClient.post('/sales/additemGeneric', {
            product_id: item.product_id,
            quantity: item.quantity,
            total: item.price,
            price: item.price,
            sale_id: saleId,
            product_name: item.product_name,
            is_generic: true,
            ...(item.item_discount_value ? { item_discount_value: item.item_discount_value } : {})
        });        
        
        return response;
    }

    /**
     * Atualiza um produto genérico no carrinho
     * @param {Object} item - Dados do produto genérico
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async updateItem(item, saleId) {        
        return await httpClient.post('/sales/updateitemGeneric', {
            product_id: item.product_id,
            Quantity: item.quantity,
            total: item.price,
            product_name: item.product_name,
            original_product_name: item.original_product_name,
            price: item.value,
            sale_id: saleId,
            is_generic: true,
            ...(item.item_discount_value ? { item_discount_value: item.item_discount_value } : {})
        });
    }

    /**
     * Incrementa a quantidade de um produto genérico
     * @param {string} saleId - ID da venda
     * @param {Object} data - Dados do produto
     * @returns {Promise<Object>} Resposta da API
     */
    async incrementItem(saleId, data) {
        return await httpClient.post(`/sales/${saleId}/incrementitemGeneric`, data);
    }

    /**
     * Decrementa a quantidade de um produto genérico
     * @param {string} saleId - ID da venda
     * @param {Object} data - Dados do produto
     * @returns {Promise<Object>} Resposta da API
     */
    async decrementItem(saleId, data) {
        return await httpClient.post(`/sales/${saleId}/decrementitemGeneric`, data);
    }

    /**
     * Remove um produto genérico do carrinho
     * @param {string} saleId - ID da venda
     * @param {Object} data - Dados do produto
     * @returns {Promise<Object>} Resposta da API
     */
    async deleteItem(saleId, data) {
        return await httpClient.post(`/sales/${saleId}/removeGenericItem`, data);
    }

    /**
     * Verifica se um produto é genérico
     * @param {Object} product - Produto a ser verificado
     * @returns {boolean} True se for genérico
     */
    isGenericProduct(product) {
        return product.is_generic || product.isGeneric;
    }

    /**
     * Cria um produto genérico a partir dos dados do formulário
     * @param {Object} formData - Dados do formulário
     * @returns {Object} Produto genérico formatado
     */
    createGenericProduct(formData) {
        // Gerar um ID único para produtos genéricos se não existir
        const productId = formData.product_id || `generic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        return {
            product_id: productId,
            product_name: formData.product_name.trim(),
            price: formData.price,
            quantity: formData.quantity,
            is_generic: true,
            ...(formData.item_discount_value ? { item_discount_value: formData.item_discount_value } : {})
        };
    }

    /**
     * Verifica se já existe um produto genérico com o mesmo nome no carrinho
     * @param {Array} cart - Carrinho de compras
     * @param {string} productName - Nome do produto
     * @returns {Object|null} Produto existente ou null
     */
    findExistingGenericItem(cart, productName) {
        return cart.find(item => 
            item.is_generic && 
            item.product_name && 
            item.product_name.toLowerCase().trim() === productName.toLowerCase().trim()
        );
    }

    /**
     * Atualiza um item genérico existente no carrinho
     * @param {Object} existingItem - Item existente no carrinho
     * @param {Object} newProduct - Novo produto
     * @param {number} quantity - Quantidade a adicionar
     * @returns {Object} Item atualizado
     */
    updateExistingItem(existingItem, newProduct, quantity) {
        const newQuantity = existingItem.quantity + quantity;
        const newPrice = parseFloat(newProduct.price);
        return {
            ...existingItem,
            quantity: newQuantity,
            price: newProduct.price, // Atualizar preço se diferente
            total: newQuantity * newPrice // Calcular total corretamente
        };
    }

    /**
     * Verifica se um produto genérico deve ser atualizado ou adicionado como novo
     * @param {Array} cart - Carrinho de compras
     * @param {Object} product - Produto a ser verificado
     * @returns {Object} { shouldUpdate: boolean, existingItem: Object|null }
     */
    shouldUpdateGenericProduct(cart, product) {
        if (!this.isGenericProduct(product)) {
            return { shouldUpdate: false, existingItem: null };
        }

        const existingItem = this.findExistingGenericItem(cart, product.product_name);
        
        return {
            shouldUpdate: !!existingItem,
            existingItem: existingItem
        };
    }

    /**
     * Valida os dados de um produto genérico
     * @param {Object} productData - Dados do produto
     * @returns {Object} Resultado da validação
     */
    validateGenericProduct(productData) {
        const errors = [];
        
        console.log('Validando produto genérico:', productData);
        
        if (!productData.product_name || !productData.product_name.trim()) {
            errors.push('Nome do produto é obrigatório');
        }
        
        if (!productData.price || productData.price === '0,00') {
            errors.push('Preço deve ser maior que zero');
        }
        
        if (!productData.quantity || parseInt(productData.quantity) <= 0) {
            errors.push('Quantidade deve ser maior que zero');
        }

        console.log('Erros de validação:', errors);
        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

export default new GenericProductService();
